import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    q: "When and where is the Aloha Tequila & Arts Festival?",
    a: "The 1st Annual Aloha Tequila & Arts Festival takes place on Saturday, June 20th, 2026 from 5:00 PM to 9:00 PM at International Market Place, 2330 Kalakaua Ave, Waikiki, Honolulu, Hawaii 96815. It's the headline event of Oahu's Father's Day weekend (June 19–21)."
  },
  {
    q: "What's happening on Oahu Father's Day weekend 2026?",
    a: "Father's Day weekend 2026 runs June 19–21, with Father's Day itself on Sunday, June 21. The marquee Oahu event that weekend is the Aloha Tequila & Arts Festival on Saturday, June 20 from 5–9 PM at International Market Place in Waikiki. 21+, 80+ premium tequilas, mezcal, agave spirits, live music, Hawaiian art, and street food — a perfect Father's Day gift or weekend trip."
  },
  {
    q: "What's included with my ticket?",
    a: "Your ticket gets you access to 80+ premium tequilas, mezcals and agave spirits tastings, live music and DJs, Hawaiian art and live painting, curated street food from top local restaurants, taco stations, a flea market, and exclusive VIP sections. Must be 21+ with valid ID."
  },
  {
    q: "Is the Aloha Tequila Festival a fundraiser?",
    a: "Yes. Partial proceeds benefit the Aloha Cancer Project (alohacancerproject.org) — a Honolulu-based 501(c)(3) supporting cancer research, patient services, and community outreach in Hawaii."
  },
  {
    q: "What kind of tequila and agave spirits will be there?",
    a: "Featured tasting brands include Kapena, Komoa, Don Fulano, G4, Herradura, Tequila Ocho, Código, El Tesoro, Fortaleza, Cuervo (Tradicional & Reserva de la Familia), Maestro Dobel, El Jimador, Volcán, Gran Centenario, Casa Azul, Casa Obsidiana, La Gritona, Terralta, Santaleza, Milagro, Mandala, Partida, Paladar, 3 Tres, Espolón, Corralejo, Cascahuín — plus mezcal & sotol (Xicaru, 400 Conejos, Agave de Cortes, El Jolgorio, Sotol Por Siempre). Sections include Additive-Free Alliance tequilas, Big Name Brands, Mezcal/Sotol/Bacanora, Mixto cocktails, and RTD selections."
  },
  {
    q: "How do I buy tickets?",
    a: "Tickets are sold exclusively through TicketSpice. Click any 'Get Tickets' button on this site to checkout securely at alohacancer.ticketspice.com/aloha-tequila-arts-festival."
  },
  {
    q: "Are there vendor and sponsor opportunities?",
    a: "Yes. Food vendors, retail vendors, business sponsors (Title, Diamond, Platinum, Gold, Silver, Bronze tiers), and beverage brand exhibitors (Añejo, Reposado, Blanco) can apply through our Vendors/Sponsors page. Email everything@8eight8.net or apply online for current availability."
  }
];

const HomeFAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="home-faq" className="py-20 sm:py-24 bg-gradient-to-b from-gray-950 to-black relative overflow-hidden" data-testid="home-faq-section">
      {/* Decorative blobs */}
      <div className="absolute top-1/3 -left-32 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />

      <div className="max-w-4xl mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30 mb-4">
            <HelpCircle className="w-4 h-4 text-pink-400" />
            <span className="text-pink-300 text-sm font-medium tracking-wider uppercase">Quick Answers</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 mb-4">
            Festival FAQs
          </h2>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            Everything you need to know about Hawaii&apos;s premier tequila festival in Waikiki.
          </p>
        </div>

        <div className="space-y-3">
          {FAQS.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className={`rounded-2xl border transition-all duration-300 ${
                  isOpen
                    ? 'bg-gradient-to-br from-pink-500/10 to-orange-500/10 border-pink-500/40 shadow-lg shadow-pink-500/10'
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                }`}
                data-testid={`home-faq-item-${i}`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 sm:py-5 text-left"
                  data-testid={`home-faq-toggle-${i}`}
                >
                  <h3 className="text-white font-semibold text-base sm:text-lg pr-2">{item.q}</h3>
                  <ChevronDown
                    className={`w-5 h-5 text-pink-400 flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-300 ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 sm:px-6 pb-5 text-white/75 leading-relaxed text-sm sm:text-base">
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/faq"
            data-testid="home-faq-see-all-link"
            className="inline-flex items-center gap-2 text-pink-400 hover:text-orange-400 transition-colors font-semibold"
          >
            See all FAQs
            <span aria-hidden>→</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeFAQSection;
